import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { ExecutiveClimateCountsDto, ExecutivePulseResponse, ExecutiveReportRowDto } from '../../shared/api-types.js';
import { CLIMATE_SIGNALS, CLIMATE_SIGNAL_TITLES, DISCUSSION_CLIMATES, DISCUSSION_CLIMATE_LABELS, type ClimateSignal } from '../../shared/enums.js';
import { api } from '../api.js';
import { PATHS } from '../paths.js';
import { Button, DataTable, EM_DASH, Page, PageHead, Section, StatTile, StatTileRow, StateView, failureDetail, formatCount, sortOptionsFor, useRequest, useUrlSort, type DataTableColumn } from '../ui/index.js';
import { AuthorLine, ExecutiveFailure, ExecutiveStrip } from './executive-parts.js';
import { ReportStatePill } from './executive-report.js';
import {
  CLIMATE_NOT_COUNTED_TITLE,
  LOADING_ALL_REPORTS,
  NO_REPORT_TITLE,
  SIGNALS_NOTE,
  SLACK_BELOW_FORMER_MINIMUM_PULSE,
  SLACK_NO_MARKS_PULSE,
  SLACK_NOT_COUNTED_TITLE,
  SLACK_OFF_PULSE,
  previousWeekHint,
  reportName,
  reportsExtentText,
  signalTodaySortValue,
  signalTodayText,
  signalWeekSortValue,
  signalWeekText,
  climateNotCountedSentence,
  slackBasisText,
  SLACK_NOT_COUNTED_RULE,
  thinSentence,
  topSignalsText,
  trendRowNote,
  trendSlackNote,
  weekOf,
  weekStartOf,
  weekText,
  windowText,
} from './lib/executive.js';
import { useCursorPages, type CursorPage } from './lib/use-cursor-pages.js';

// The Pulse section (ADR 0019; design §2.13 "Pulse"; the owner's pick E12, tiles and sortable tables first, the chart
// later — stage 8): the latest readable report's climate as eight tiles — the four meeting-climate counts and, wherever any
// member's Slack looks were counted (every member connected to Slack with both Slack permissions, whether or not Calis makes
// their summaries — the owner's ruling of 2026-10-13 on ADR 0019's Open 56, "Count them too"), the four Slack-climate
// counts — each with its comparison basis, the previous
// calendar week (the owner's "Compare fixed weeks"); the seven warning signals as a sortable table; and the trend, one row
// a report, paged.
//
// What the route returns is what is shown, every count exact (the owner's ruling of 2026-10-12, "Remove them everywhere":
// "Exact counts everywhere"; no "Rare", no minimum); a report with no climate count "Climate not counted", and why where it
// recorded why; where Slack was not counted, which reason applies as the report recorded it (Open 45), and the rule it is
// counted under where it recorded none; and a withheld report its two counts (zero and zero since that ruling) and why it
// was withheld as its executor recorded it (the owner's rulings of 2026-10-13 on Opens 50 and 54, "Record the reasons"),
// with its Slack climate counted all the same. A previous week with nothing counted reads "Not counted", never "no report". No
// chart: 04-data §2.2's floor is stage 8's.

/**
 * The tiles' content floor (04-data §3: "derived from the measured container and a content floor"): a count of up to
 * "9,999" at the tile's type size, with its label, two to a line inside a 320px viewport less its 16px gutters (288px).
 */
export const PULSE_TILE_MIN = '8.5rem';
const TREND_PAGE = 30;

export function PulseSection() {
  const request = useRequest<ExecutivePulseResponse>((signal) => api.get<ExecutivePulseResponse>(`/api/executive/pulse?limit=${TREND_PAGE}`, { signal }), []);
  // Why a failure is shown whenever the current read failed (the stage 6 review; 03-components §1): a retry that fails must
  // not leave the figures it was asked to replace standing as current.
  const failed = request.status === 'error' && request.error !== null;
  const data = failed ? null : request.data;
  return (
    <Page measure="wide" className="executive-pulse">
      <PageHead title="Pulse" subtitle={data?.report ? pulseSubtitle(data) : undefined} strip={<ExecutiveStrip />} />
      {request.status === 'loading' && !request.data ? <StateView kind="loading" label="Loading the pulse" minHeight="24rem" /> : null}
      {failed && request.error ? <ExecutiveFailure failure={request.error} what="the pulse" onRetry={request.reload} /> : null}
      {data ? <PulseBody data={data} /> : null}
      <AuthorLine />
    </Page>
  );
}

/** One sentence (02-patterns §2.1): which report the pulse reads, by its window, and the week it is set beside. */
function pulseSubtitle(data: ExecutivePulseResponse): string {
  const report = data.report!;
  const window = windowText(report.windowStart, report.windowEnd);
  return data.comparedWith
    ? `The latest report’s climate, ${window}, beside the previous week, ${weekText(data.comparedWith.weekStart, data.comparedWith.weekEnd)}.`
    : `The latest report’s climate, ${window}.`;
}

function PulseBody({ data }: { data: ExecutivePulseResponse }) {
  return (
    <>
      <Section id="meeting-climate" title="Meetings" description="How many of the report’s meetings were given each climate word.">
        <MeetingClimate data={data} />
      </Section>
      <Section id="slack-climate" title="Slack" description="How many of the report’s Slack looks were given each climate word.">
        <SlackClimate data={data} />
      </Section>
      <Signals data={data} />
      <Trend data={data} />
    </>
  );
}

function ClimateTiles({ today, previousWeek }: { today: ExecutiveClimateCountsDto; previousWeek: ExecutiveClimateCountsDto | null }) {
  return (
    <StatTileRow minWidth={PULSE_TILE_MIN} className="executive-tiles">
      {DISCUSSION_CLIMATES.map((climate) => (
        <StatTile
          key={climate}
          label={DISCUSSION_CLIMATE_LABELS[climate]}
          value={today[climate]}
          hint={previousWeekHint(previousWeek ? previousWeek[climate] : null)}
        />
      ))}
    </StatTileRow>
  );
}

function MeetingClimate({ data }: { data: ExecutivePulseResponse }) {
  const meetings = data.meetings;
  switch (meetings.state) {
    case 'counted':
      return <ClimateTiles today={meetings.today} previousWeek={meetings.previousWeek} />;
    case 'not_counted':
      // Why the recorded cause (the owner's ruling of 2026-10-13 on ADR 0019's Open 54, "Record the reasons"): the page states
      // the cause, and the rule only where the report recorded none.
      return <StateView kind="empty" icon="info" title={CLIMATE_NOT_COUNTED_TITLE} description={climateNotCountedSentence(meetings.reason)} compact />;
    case 'insufficient':
      // Why "No report written" and never "Not enough material" (the owner's ruling of 2026-10-12): no minimum applies. Why
      // the reason (his ruling of 2026-10-13 on Open 50, "Record the reasons"): the exact cause, as the record says it.
      return <StateView kind="empty" icon="info" title="No report written" description={thinSentence({ people: meetings.people, meetings: meetings.meetings, reason: meetings.reason })} compact />;
    case 'none':
    default:
      return <StateView kind="empty" icon="clock" title={NO_REPORT_TITLE} description="The pulse is read from the latest report once one is written." compact />;
  }
}

function SlackClimate({ data }: { data: ExecutivePulseResponse }) {
  const slack = data.slack;
  if (slack.state === 'counted') {
    return (
      <div className="stack stack--2">
        <ClimateTiles today={slack.today} previousWeek={slack.previousWeek} />
        <p className="text-sm secondary">{`${slackBasisText({ state: 'counted', looks: slack.looks, sweeps: slack.sweeps, checks: slack.checks, people: slack.people })}.`}</p>
      </div>
    );
  }
  // Why the reason the report recorded (the owner's ruling of 2026-10-09 on ADR 0019's Open 45, "Say why Slack wasn't counted";
  // a withheld report's too since his ruling of 2026-10-13 on Open 54, "Record the reasons"; lib/executive.ts): Slack climate
  // off, or no Slack climate in the window from any member connected to Slack (AI summaries or not since his ruling of
  // 2026-10-13 on Open 56, "Count them too"), each said as such; on a report written before 2026-10-12, the minimum then in
  // force, with no number; a report that recorded no reason keeps the rule and claims none.
  if (slack.state === 'not_counted') {
    if (slack.reason === 'slack_off') return <StateView kind="empty" icon="info" title={SLACK_NOT_COUNTED_TITLE} description={SLACK_OFF_PULSE} compact />;
    if (slack.reason === 'no_marks') return <StateView kind="empty" icon="info" title={SLACK_NOT_COUNTED_TITLE} description={SLACK_NO_MARKS_PULSE} compact />;
    if (slack.reason === 'below_minimum') return <StateView kind="empty" icon="info" title={SLACK_NOT_COUNTED_TITLE} description={SLACK_BELOW_FORMER_MINIMUM_PULSE} compact />;
    return <StateView kind="empty" icon="info" title={SLACK_NOT_COUNTED_TITLE} description={SLACK_NOT_COUNTED_RULE} compact />;
  }
  return <StateView kind="empty" icon="info" title={SLACK_NOT_COUNTED_TITLE} description="The pulse is read from the latest report once one is written." compact />;
}

interface SignalRow {
  id: ClimateSignal;
  today: ExecutivePulseResponse['signals'][number]['today'];
  previousWeek: ExecutivePulseResponse['signals'][number]['previousWeek'];
}

/**
 * The seven warning signals (design §2.13: today · previous week; in the URL under `signals_`). Why no "change" column:
 * the latest report counts one window and the previous week sums seven days of reports, so their difference has no
 * direction to show — 04-data §3: "a delta without a baseline is decoration"; the week is shown as its own column instead.
 */
function Signals({ data }: { data: ExecutivePulseResponse }) {
  const rows = useMemo<SignalRow[]>(() => data.signals.map((entry) => ({ id: entry.signal, today: entry.today, previousWeek: entry.previousWeek })), [data]);
  const columns = useMemo<DataTableColumn<SignalRow>[]>(() => [
    { key: 'signal', header: 'Signal', card: 'title', type: 'enum', directionWords: { asc: 'as listed', desc: 'listed last first' }, sortValue: (row) => CLIMATE_SIGNALS.indexOf(row.id), cell: (row) => CLIMATE_SIGNAL_TITLES[row.id] },
    { key: 'today', header: 'Latest report', type: 'number', numeric: true, sortValue: (row) => signalTodaySortValue(row.today), cell: (row) => signalTodayText(row.today) },
    { key: 'previous_week', header: 'Previous week (total)', name: 'Previous week', type: 'number', numeric: true, sortValue: (row) => signalWeekSortValue(row.previousWeek), cell: (row) => signalWeekText(row.previousWeek) },
  ], []);
  return (
    <Section id="signals" title="Warning signals" description={SIGNALS_NOTE}>
      <DataTable
        caption="Warning signals"
        failureNoun="the warning signals"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        sort={{ defaultSort: { key: 'today', dir: 'desc' }, namespace: 'signals' }}
        clientSort
        mobile="scroll"
        empty={{ title: 'No signals', description: 'The seven warning signals are listed once a report is written.' }}
      />
    </Section>
  );
}

const climateCell = (counts: ExecutiveClimateCountsDto | null, climate: (typeof DISCUSSION_CLIMATES)[number]) => (counts === null ? EM_DASH : formatCount(counts[climate]));

/**
 * The trend (design §2.13): one row a report — a refreshed day in its parts — with its people, meetings, Slack looks, the
 * eight climate counts and its top signals; a withheld report shows its two counts, why it was withheld (`trendRowNote`: the
 * reason its executor recorded, or, withheld before the reason was kept, a minimum since removed where it held back counts,
 * else that its reason was not recorded) where the meeting climate would be, and the Slack climate it counted all the same;
 * and each absent count says why where its report recorded why (`trendRowNote`, `trendSlackNote`: the owner's rulings of
 * 2026-10-13 on ADR 0019's Opens 50 and 54, "Record the reasons" — the cause, not the rule). Why a Week column and not group headers: the table is grouped by week by its order and says each row's week,
 * since a grouping drawn by a gap alone is never conformant (04-data §1.6). Paged, latest first, under `trend_`.
 */
function Trend({ data }: { data: ExecutivePulseResponse }) {
  const pages = useCursorPages<ExecutiveReportRowDto>(data.trend as CursorPage<ExecutiveReportRowDto>, (before) =>
    api.get<ExecutivePulseResponse>(`/api/executive/pulse?limit=${TREND_PAGE}&before=${encodeURIComponent(before)}`).then((result) => (result.ok ? { ...result, data: result.data.trend } : result)),
  );
  const columns = useMemo<DataTableColumn<ExecutiveReportRowDto>[]>(() => [
    {
      key: 'window_end',
      header: 'Report',
      type: 'date',
      card: 'title',
      sortValue: (row) => row.windowEnd,
      cell: (row) => (
        <span className="stack stack--1">
          <Link to={PATHS.executiveReport(row.id)} className="hit-area">{reportName(row)}</Link>
          {trendRowNote(row) ? <span className="text-xs secondary">{trendRowNote(row)}</span> : null}
          {trendSlackNote(row) ? <span className="text-xs secondary">{trendSlackNote(row)}</span> : null}
        </span>
      ),
    },
    { key: 'week', header: 'Week of', name: 'Week', type: 'date', card: 'meta', fold: 8, sortValue: (row) => weekStartOf(row.windowEnd), cell: (row) => weekOf(row.windowEnd) },
    { key: 'state', header: 'State', type: 'enum', directionWords: { asc: 'completed first', desc: 'skipped first' }, fold: 2, sortValue: (row) => (row.status === 'ready' ? 0 : 1), cell: (row) => <ReportStatePill status={row.status} /> },
    { key: 'people', header: 'People', type: 'number', numeric: true, fold: 14, sortValue: (row) => row.people, cell: (row) => formatCount(row.people) },
    { key: 'meetings', header: 'Meetings', type: 'number', numeric: true, fold: 13, sortValue: (row) => row.meetings, cell: (row) => formatCount(row.meetings) },
    ...DISCUSSION_CLIMATES.map((climate, index): DataTableColumn<ExecutiveReportRowDto> => ({
      key: climate,
      header: DISCUSSION_CLIMATE_LABELS[climate],
      type: 'number',
      numeric: true,
      fold: 12 - index,
      sortValue: (row) => row.climate?.[climate] ?? null,
      cell: (row) => climateCell(row.climate, climate),
    })),
    { key: 'slack_looks', header: 'Slack looks', type: 'number', numeric: true, fold: 7, sortValue: (row) => row.slackLooks, cell: (row) => (row.slackLooks === null ? EM_DASH : formatCount(row.slackLooks)) },
    ...DISCUSSION_CLIMATES.map((climate, index): DataTableColumn<ExecutiveReportRowDto> => ({
      key: `slack_${climate}`,
      // Why the label as written (05-content §1, §8; the Phase 1c audit's D30): a label and its value, as a filter chip
      // reads — never the climate's word lowercased into a phrase.
      header: `Slack: ${DISCUSSION_CLIMATE_LABELS[climate]}`,
      type: 'number',
      numeric: true,
      fold: 6 - index,
      sortValue: (row) => row.slackClimate?.[climate] ?? null,
      cell: (row) => climateCell(row.slackClimate, climate),
    })),
    // Why it folds first: it is read more than compared. Sortable all the same (04-data §1.2 exempts free-form content
    // alone, and this is up to three closed words): by the words shown, a report with none last in either direction.
    { key: 'top_signals', header: 'Top signals', fold: 1, sortValue: (row) => (row.topSignals.length === 0 ? null : topSignalsText(row.topSignals)), cell: (row) => topSignalsText(row.topSignals) },
  ], []);
  const sortSpec = useMemo(() => ({ defaultSort: { key: 'window_end', dir: 'desc' as const }, namespace: 'trend' }), []);
  const [sort] = useUrlSort(sortOptionsFor(columns, sortSpec));
  const sortedOtherwise = sort.key !== 'window_end' || sort.dir !== 'desc';
  // Why not after a failure: the failure is said beneath the table, and the reader's "Load earlier reports" retries it —
  // never a loop of requests against a route that is refusing them.
  const { more, loading, loadAll, error: pageError } = pages;
  useEffect(() => {
    if (sortedOtherwise && more && !loading && pageError === null) loadAll();
  }, [sortedOtherwise, more, loading, pageError, loadAll]);
  return (
    <Section id="trend" title="Trend" description="Each report’s climate, latest first; a refreshed day appears in its parts. A dash is a count not made.">
      <DataTable
        caption="Trend"
        failureNoun="the trend"
        columns={columns}
        rows={pages.rows}
        rowKey={(row) => row.id}
        sort={sortSpec}
        clientSort
        rowLabel={(row) => reportName(row)}
        empty={{ title: NO_REPORT_TITLE, description: 'Each report adds a row here.' }}
        footer={
          <div className="audit-footer">
            <span>{sortedOtherwise && pages.more && !pages.error ? LOADING_ALL_REPORTS : reportsExtentText(pages.rows.length, pages.more)}</span>
            {pages.more && (!sortedOtherwise || pages.error) ? <Button size="sm" variant="secondary" onClick={pages.error && sortedOtherwise ? pages.loadAll : pages.loadMore} loading={pages.loading}>Load earlier reports</Button> : null}
          </div>
        }
      />
      {/* Why `failureDetail` and never the server's message raw (CLAUDE.md; 05-content §4). */}
      {pages.error ? <p className="field__error" role="alert">We couldn’t load the earlier reports. {failureDetail(pages.error)}</p> : null}
    </Section>
  );
}
